import {
    Avatar,
    Box,
    Circle,
    Divider,
    Flex,
    HStack,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SessionContext from '../contexts/SessionContext';
import { useSubmitLogout } from '../hooks/useSubmitLogout';
import { useGetProfile } from '../hooks/useGetProfile';
import InviteUserModal from '../modals/InviteUserModal';
import { SendAnnouncementModal } from './notifications';
import { PresenceStatusToggle, AwayStatusPanel } from './PresenceStatusMenu';
import OrganizationSwitcherPanel from './OrganizationSwitcherPanel';
import { Icon as YaxleIcon } from '../ui';
import {
    MenuRow,
    TokenChip,
    checkboxMenuPopoverContentSx,
} from '../ui/primitives/CheckboxMenu';

interface UserMenuProps {
    /** Rail collapsed: trigger shrinks to the avatar only. */
    isCollapsed?: boolean;
}

/**
 * Account block at the head of the sidebar (ENG-2297) — full name, org, and
 * the profile dropdown that used to live in the TopBar. Notifications and
 * messages stay in the TopBar; everything account-scoped lives here.
 */
const UserMenu = ({ isCollapsed = false }: UserMenuProps) => {
    const navigate = useNavigate();
    const sessionContext = useContext(SessionContext);
    const submitLogout = useSubmitLogout();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    /** Root profile menu vs Teams-style inset panels: Away status, or (employees) the org switcher. */
    const [profileMenuView, setProfileMenuView] = useState<'root' | 'away' | 'organizations'>('root');
    /** Widen flyout when Custom Away shows DualMonthRangeCalendar. */
    const [awayCustomMode, setAwayCustomMode] = useState(false);
    const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false);
    const [isSendAnnouncementOpen, setIsSendAnnouncementOpen] = useState(false);
    const { data: profile } = useGetProfile();
    const isAway = profile?.presenceStatus === 'away';
    /** Admins can broadcast org-wide announcements. */
    const canSendAnnouncements = sessionContext?.isAdmin === true;
    /** Yaxle employee account: can switch into any customer org. */
    const isInternal = sessionContext?.isAuthenticated === true && sessionContext.isInternal;
    const internalChipLabel = sessionContext?.isAuthenticated && sessionContext.isAdmin ? 'Yaxle Admin' : 'Yaxle';
    const canManageTeam = sessionContext?.isAuthenticated === true && sessionContext.canManageInternalTeam;

    /**
     * One Settings entry, like main's rework: the takeover page owns the split
     * between personal and organization sections. Without org-settings access
     * there is no overview to land on, so go straight to Profile.
     */
    const settingsPath =
        sessionContext?.isAuthenticated &&
            sessionContext.permissions.some(
                (p) => p === 'settings' || p === 'preferences' || p === 'it-settings',
            )
            ? '/settings'
            : '/settings/profile';

    const handleLogout = () => {
        setIsPopoverOpen(false);
        submitLogout.mutate();
        navigate('/login');
    };

    const fullName = `${sessionContext?.firstName || ''} ${sessionContext?.lastName || ''}`.trim() || 'User';

    const avatar = (
        <Box position="relative" flexShrink={0}>
            <Avatar
                size="sm"
                name={fullName}
                bg="#0071E3"
                color="white"
                fontSize="xs"
                fontWeight="semibold"
            />
            <Circle
                size="10px"
                bg={isAway ? 'orange.400' : 'green.400'}
                borderWidth="2px"
                borderColor="white"
                position="absolute"
                bottom="0"
                right="0"
            />
        </Box>
    );

    return (
        <>
            <Popover
                placement="bottom-start"
                isOpen={isPopoverOpen}
                onOpen={() => {
                    setProfileMenuView('root');
                    setAwayCustomMode(false);
                    setIsPopoverOpen(true);
                }}
                onClose={() => {
                    setIsPopoverOpen(false);
                    setProfileMenuView('root');
                    setAwayCustomMode(false);
                }}
                // Keep open while configuring Away so nested duration menu clicks don't dismiss it.
                closeOnBlur={profileMenuView !== 'away'}
            >
                <PopoverTrigger>
                    <HStack
                        as="button"
                        type="button"
                        aria-label="Account menu"
                        spacing={2}
                        align="center"
                        cursor="pointer"
                        _hover={{ bg: 'gray.200' }}
                        borderRadius="md"
                        p={isCollapsed ? 1.5 : 2}
                        w={isCollapsed ? 'auto' : '100%'}
                        minW={0}
                        justifyContent={isCollapsed ? 'center' : 'flex-start'}
                        transition="background-color 180ms ease"
                    >
                        {isCollapsed ? (
                            <Tooltip label={fullName} placement="right" openDelay={150}>
                                {avatar}
                            </Tooltip>
                        ) : (
                            <>
                                {avatar}
                                <VStack spacing={0} align="flex-start" minW={0} flex="1">
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.900" noOfLines={1} w="100%" textAlign="left">
                                        {fullName}
                                    </Text>
                                    {sessionContext?.organizationName && (
                                        <HStack spacing={1.5} w="100%" minW={0}>
                                            <Text fontSize="xs" color="gray.500" noOfLines={1} textAlign="left" minW={0}>
                                                {sessionContext.organizationName}
                                            </Text>
                                            {isInternal && <TokenChip>{internalChipLabel}</TokenChip>}
                                        </HStack>
                                    )}
                                </VStack>
                                <YaxleIcon
                                    name="chevronDown"
                                    size="0.5rem"
                                    color="fg.muted"
                                    flexShrink={0}
                                    alignSelf="flex-end"
                                    mb="2px"
                                    transition="transform 200ms ease"
                                    transform={isPopoverOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                                />
                            </>
                        )}
                    </HStack>
                </PopoverTrigger>
                <Portal>
                    <PopoverContent
                        w={
                            profileMenuView === 'away' && awayCustomMode
                                ? '560px'
                                : profileMenuView === 'organizations'
                                    ? '320px'
                                    : '280px'
                        }
                        maxW="calc(100vw - 24px)"
                        zIndex={2000}
                        // Shared menu chrome: white paper, gray hairline, no shadow.
                        sx={checkboxMenuPopoverContentSx}
                    >
                        <PopoverBody p={0}>
                            {profileMenuView === 'organizations' ? (
                                <OrganizationSwitcherPanel
                                    onBack={() => setProfileMenuView('root')}
                                    onSwitched={() => {
                                        setIsPopoverOpen(false);
                                        setProfileMenuView('root');
                                    }}
                                />
                            ) : profileMenuView === 'away' ? (
                                <Box p={4}>
                                    <AwayStatusPanel
                                        onBack={() => {
                                            setAwayCustomMode(false);
                                            setProfileMenuView('root');
                                        }}
                                        onDone={() => {
                                            setAwayCustomMode(false);
                                            setProfileMenuView('root');
                                        }}
                                        onCustomModeChange={setAwayCustomMode}
                                    />
                                </Box>
                            ) : (
                                <>
                                    {/* Identity: one glance — who, where, what role. */}
                                    <Flex gap={3} px="14px" pt="14px" pb="12px" align="center">
                                        <Avatar
                                            size="md"
                                            name={fullName}
                                            bg="#0071E3"
                                            color="white"
                                            fontSize="sm"
                                            fontWeight="semibold"
                                        />
                                        <Box minW={0} flex="1">
                                            <HStack spacing={2} align="center">
                                                <Text fontSize="sm" fontWeight="semibold" color="gray.900" noOfLines={1}>
                                                    {fullName}
                                                </Text>
                                                {isInternal
                                                    ? <TokenChip>{internalChipLabel}</TokenChip>
                                                    : sessionContext?.isAdmin && <TokenChip>Admin</TokenChip>}
                                            </HStack>
                                            <Text fontSize="xs" color="gray.500" noOfLines={1} mt="2px">
                                                {[sessionContext?.organizationName, sessionContext?.locationName]
                                                    .filter(Boolean)
                                                    .join(' · ') || 'Organization'}
                                            </Text>
                                        </Box>
                                    </Flex>

                                    {isInternal && (
                                        <Box px="6px" pb="6px">
                                            <MenuRow
                                                kind="drillIn"
                                                icon={<YaxleIcon name="swap" size="sm" />}
                                                summary="Switch"
                                                onClick={() => setProfileMenuView('organizations')}
                                            >
                                                {sessionContext?.organizationName || 'Organization'}
                                            </MenuRow>
                                            {canManageTeam && (
                                                <MenuRow
                                                    kind="single"
                                                    icon={<YaxleIcon name="users" size="sm" />}
                                                    onClick={() => {
                                                        setIsPopoverOpen(false);
                                                        navigate('/settings/yaxle-team');
                                                    }}
                                                >
                                                    Yaxle team
                                                </MenuRow>
                                            )}
                                        </Box>
                                    )}

                                    <Divider borderColor="gray.200" />

                                    <Box px="14px" py="12px">
                                        <PresenceStatusToggle
                                            onOpenAway={() => {
                                                setAwayCustomMode(false);
                                                setProfileMenuView('away');
                                            }}
                                        />
                                    </Box>

                                    <Divider borderColor="gray.200" />

                                    <Box p="6px">
                                        <MenuRow
                                            kind="single"
                                            icon={<YaxleIcon name="settings" size="sm" />}
                                            onClick={() => {
                                                setIsPopoverOpen(false);
                                                navigate(settingsPath);
                                            }}
                                        >
                                            Settings
                                        </MenuRow>
                                        {sessionContext?.isAdmin && (
                                            <MenuRow
                                                kind="single"
                                                icon={<YaxleIcon name="plus" size="sm" />}
                                                onClick={() => {
                                                    setIsPopoverOpen(false);
                                                    setIsInviteUserModalOpen(true);
                                                }}
                                            >
                                                Add User
                                            </MenuRow>
                                        )}
                                        {canSendAnnouncements && (
                                            <MenuRow
                                                kind="single"
                                                icon={<YaxleIcon name="megaphone" size="sm" />}
                                                onClick={() => {
                                                    setIsPopoverOpen(false);
                                                    setIsSendAnnouncementOpen(true);
                                                }}
                                            >
                                                Send Announcement
                                            </MenuRow>
                                        )}
                                    </Box>

                                    <Divider borderColor="gray.200" />

                                    <Box p="6px">
                                        {/* MenuRow geometry, destructive palette. */}
                                        <Flex
                                            as="button"
                                            type="button"
                                            align="center"
                                            gap="10px"
                                            h="34px"
                                            w="100%"
                                            px="10px"
                                            borderRadius="7px"
                                            color="red.500"
                                            _hover={{ bg: 'red.50', color: 'red.600' }}
                                            onClick={handleLogout}
                                        >
                                            <YaxleIcon name="logout" size="sm" />
                                            <Text fontSize="13px" fontWeight={400} lineHeight={1.4}>
                                                Sign out
                                            </Text>
                                        </Flex>
                                    </Box>
                                </>
                            )}
                        </PopoverBody>
                    </PopoverContent>
                </Portal>
            </Popover>

            <InviteUserModal
                isOpen={isInviteUserModalOpen}
                onClose={() => setIsInviteUserModalOpen(false)}
            />
            <SendAnnouncementModal
                isOpen={isSendAnnouncementOpen}
                onClose={() => setIsSendAnnouncementOpen(false)}
            />
        </>
    );
};

export default UserMenu;
